import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Key, Eye, EyeOff, Loader2, Smartphone, Moon, PlayCircle, FileText, Sparkles } from 'lucide-react';
import {
  isNewYearSeason,
  isNewYearInterfaceEnabled,
  writeNewYearInterfaceEnabled,
  playHolidayChime,
  NEW_YEAR_PREVIEW_CHANGED_EVENT,
  NEW_YEAR_PREVIEW_STORAGE_KEY,
  NEW_YEAR_DISABLED_STORAGE_KEY
} from '../utils/newYearInterface';
import { API_URL } from '../config';
import { safeFetch } from '../utils/safeFetch';
import useModalBackButton from '../hooks/useModalBackButton';
import { haptic } from '../utils/vibration';
import useToast from '../hooks/useToast';
import FloatingField from '../components/FloatingField';
import ModalShell from '../components/ModalShell';
import { OfflineSettingsControl } from '../components/OfflineControls';
import { NotificationSettingsControl } from '../components/NotificationControls';
import { APP_VERSION } from '../generated/appVersion';
import { useInteractionPreferences } from '../contexts/InteractionPreferencesContext';
import '../styles/Management.css';

const SettingsToggleCard = ({ icon: Icon, title, description = '', active, onClick, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`w-full rounded-xl border px-3.5 py-2.5 text-left transition-all active:scale-[0.99] ${
      active
        ? 'border-blue-500 bg-blue-50 shadow-[0_6px_18px_rgba(59,130,246,0.12)] dark:bg-blue-950/20'
        : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
    } ${disabled ? 'opacity-60' : ''}`}
  >
    <div className="flex items-center gap-3">
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
          active
            ? 'bg-blue-500 text-white'
            : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300'
        }`}
      >
        <Icon className="h-[18px] w-[18px]" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</div>
        {description ? (
          <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{description}</div>
        ) : null}
      </div>
      <span
        aria-hidden="true"
        className="toggle-switch"
        style={{
          '--toggle-bg': active ? 'rgb(59 130 246)' : 'rgb(156 163 175)',
          '--toggle-height': '26px',
          '--toggle-width': '44px',
          '--knob-x': active ? '20px' : '2px'
        }}
      >
        <span className="toggle-switch-knob" />
      </span>
    </div>
  </button>
);

const isIOSDevice = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  return /iPad|iPhone|iPod/.test(ua) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const Settings = ({
  theme,
  setTheme,
  userType,
  openChangePassword = false,
}) => {
  const navigate = useNavigate();
  const {
    userAnimationsEnabled,
    reducedMotionRequested,
    setUserAnimationsEnabled,
    hapticsEnabled,
    setHapticsEnabled,
  } = useInteractionPreferences();
  const [showPasswordModal, setShowPasswordModal] = useState(openChangePassword);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const { toast, showToast, hideToast } = useToast(3500);
  const isDeveloper = (userType || localStorage.getItem('user_type')) === 'developer';
  const inSeason = isNewYearSeason();
  const canShowNewYearSetting = inSeason || isDeveloper;
  const [newYearActive, setNewYearActive] = useState(() => (
    isNewYearInterfaceEnabled({ userType })
  ));

  useEffect(() => {
    const syncState = () => {
      setNewYearActive(isNewYearInterfaceEnabled({ userType }));
    };

    const handlePreviewChange = (e) => {
      if (typeof e?.detail === 'boolean') {
        setNewYearActive(e.detail);
      } else {
        syncState();
      }
    };

    const handleStorage = (e) => {
      if (
        e.key === NEW_YEAR_PREVIEW_STORAGE_KEY
        || e.key === NEW_YEAR_DISABLED_STORAGE_KEY
      ) {
        syncState();
      }
    };

    window.addEventListener(NEW_YEAR_PREVIEW_CHANGED_EVENT, handlePreviewChange);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(NEW_YEAR_PREVIEW_CHANGED_EVENT, handlePreviewChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, [userType]);

  const handleToggleNewYear = () => {
    const nextState = !newYearActive;
    haptic('selection');
    writeNewYearInterfaceEnabled(nextState, { userType });
    setNewYearActive(nextState);
    if (nextState) {
      playHolidayChime();
      showToast('Новогоднее оформление включено 🎄', 'success');
    } else {
      showToast('Новогоднее оформление отключено', 'info');
    }
  };
  const longPressTimerRef = useRef(null);
  const pageIsDark = theme === 'dark';
  const hideHapticsSetting = isIOSDevice();
  useEffect(() => {
    // Обработчик события открытия модального окна смены пароля
    const handleOpenChangePassword = () => {
      setShowPasswordModal(true);
    };
    
    window.addEventListener('openChangePasswordModal', handleOpenChangePassword);
    
    // Очистка слушателя событий при размонтировании компонента
    return () => {
      window.removeEventListener('openChangePasswordModal', handleOpenChangePassword);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    };
  }, []);

  const handleAnimationToggle = () => {
    const newValue = !userAnimationsEnabled;
    haptic('selection');
    setUserAnimationsEnabled(newValue);
  };

  const handleHapticsToggle = () => {
    const newValue = !hapticsEnabled;
    setHapticsEnabled(newValue);
    if (newValue) haptic('selection');
  };

  const handleThemeToggle = () => {
    haptic('selection');
    setTheme(pageIsDark ? 'light' : 'dark');
  };

  const copyToClipboard = async (text, successMessage) => {
    const value = String(text || '').trim();
    if (!value) return;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = value;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      haptic('success');
      showToast(successMessage, 'success');
    } catch (error) {
      showToast('Не удалось скопировать', 'error');
    }
  };

  const startLongPress = (callback) => {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      callback();
      longPressTimerRef.current = null;
    }, 450);
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const bindLongPress = (callback) => ({
    onMouseDown: () => startLongPress(callback),
    onMouseUp: cancelLongPress,
    onMouseLeave: cancelLongPress,
    onTouchStart: () => startLongPress(callback),
    onTouchEnd: cancelLongPress,
    onTouchCancel: cancelLongPress,
    onContextMenu: (e) => e.preventDefault()
  });

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handlePasswordSubmit = async () => {
    // Валидация
    if (!passwordData.currentPassword) {
      showToast('Введите текущий пароль', 'error');
      return;
    }
    if (!passwordData.newPassword) {
      showToast('Введите новый пароль', 'error');
      return;
    }
    if (passwordData.newPassword.length < 7) {
      showToast('Новый пароль должен содержать минимум 7 символов', 'error');
      return;
    }
    if (!/[a-zа-я]/.test(passwordData.newPassword)) {
      showToast('Новый пароль должен содержать хотя бы одну маленькую букву', 'error');
      return;
    }
    if (!/[A-ZА-Я]/.test(passwordData.newPassword)) {
      showToast('Новый пароль должен содержать хотя бы одну заглавную букву', 'error');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showToast('Пароли не совпадают', 'error');
      return;
    }
    if (passwordData.currentPassword === passwordData.newPassword) {
      showToast('Новый пароль должен отличаться от текущего', 'error');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      await safeFetch(`${API_URL}/api/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        })
      });

      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      showToast('Пароль успешно изменён', 'success');
      
      setTimeout(() => {
        setShowPasswordModal(false);
      }, 1000);
      
    } catch (err) {
      console.error('Ошибка смены пароля:', err);
      showToast(err.message || 'Ошибка смены пароля', 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetPasswordModal = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setShowPasswords({
      current: false,
      new: false,
      confirm: false
    });
    setShowPasswordModal(false);
  };

  useModalBackButton(showPasswordModal, resetPasswordModal);

  return (
    <div className="settings-page space-y-5 select-none desktop-content-container">
      <div className="rounded-xl bg-white p-5 shadow-sm dark:bg-gray-900">
        <div className="flex flex-col gap-5">
          <div className="space-y-2.5">
            <SettingsToggleCard
              icon={Moon}
              title="Тёмная тема"
              active={pageIsDark}
              onClick={handleThemeToggle}
            />
            {!hideHapticsSetting && (
              <SettingsToggleCard
                icon={Smartphone}
                title="Вибрация интерфейса"
                active={hapticsEnabled}
                onClick={handleHapticsToggle}
              />
            )}
            <SettingsToggleCard
              icon={PlayCircle}
              title="Анимации интерфейса"
              description={reducedMotionRequested ? 'Сейчас ограничены настройкой системы' : ''}
              active={userAnimationsEnabled}
              onClick={handleAnimationToggle}
            />
          </div>

          <OfflineSettingsControl />

          <NotificationSettingsControl />

          {canShowNewYearSetting && (
            <SettingsToggleCard
              icon={Sparkles}
              title="Новогоднее оформление"
              active={newYearActive}
              onClick={handleToggleNewYear}
            />
          )}

          {/* App version and contact information */}
          <div className="border-t border-gray-200 pt-3 dark:border-gray-700">
            <div className="mb-2 flex justify-center">
              <button
                type="button"
                onClick={() => {
                  haptic('selection');
                  navigate('/user-agreement');
                }}
                className="inline-flex min-h-8 items-center justify-center gap-1.5 rounded-lg px-2 py-1 text-xs font-semibold text-blue-600 transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                <FileText className="h-3.5 w-3.5" />
                <span>Пользовательское соглашение</span>
              </button>
            </div>
            <div className="flex items-center justify-center space-x-2 text-gray-500 dark:text-gray-400">
              <span className="text-xs">Версия приложения: {APP_VERSION}</span>
            </div>
            <div className="mt-1.5 flex items-center justify-center space-x-1.5 text-gray-500 dark:text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5">
                <path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.14.141-.259.259-.374.261l.213-3.053 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.136-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/>
              </svg>
              <span
                className="text-xs"
                {...bindLongPress(() => copyToClipboard('@ranedell', 'Ник скопирован'))}
              >
                @ranedell
              </span>
            </div>
          </div>
        </div>
      </div>
            
      {/* Модальное окно смены пароля */}
      <ModalShell
        open={showPasswordModal}
        onClose={resetPasswordModal}
        variant="window"
        panelClassName="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900"
      >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-xl flex items-center justify-center">
                <Key className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Смена пароля
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Введите текущий и новый пароль
                </p>
              </div>
            </div>
            
            <div className="space-y-4">
              {/* Текущий пароль */}
              <div>
                <FloatingField label="Текущий пароль *">
                  <div className="relative">
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                    className="w-full select-text px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 dark:text-gray-100"
                    placeholder="Введите текущий пароль"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('current')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                  </div>
                </FloatingField>
              </div>
              
              {/* Новый пароль */}
              <div>
                <FloatingField label="Новый пароль *">
                  <div className="relative">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                    className="w-full select-text px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 dark:text-gray-100"
                    placeholder="Введите новый пароль"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                  </div>
                </FloatingField>
              </div>
              
              {/* Подтверждение пароля */}
              <div>
                <FloatingField label="Подтверждение нового пароля *">
                  <div className="relative">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                    className="w-full select-text px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 dark:text-gray-100"
                    placeholder="Повторите новый пароль"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                  </div>
                </FloatingField>
              </div>
            </div>
            
            {/* Кнопки */}
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <button
                onClick={resetPasswordModal}
                className="flex-1 px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors font-medium"
              >
                Отмена
              </button>
              <button
                onClick={handlePasswordSubmit}
                disabled={loading}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2 font-medium"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                <span>{loading ? 'Сохранение...' : 'Сменить пароль'}</span>
              </button>
            </div>
          </ModalShell>
    </div>
  );
};

export default Settings; 
